import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { login as apiLogin, getEmpresa } from '../services/api';
import api from '../services/api';

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutos
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

const AuthContext = createContext(null);

function buildUser(apiUser, empresa) {
  return {
    id:              apiUser.id,
    username:        apiUser.nombre_usuario,
    full_name:       apiUser.nombre_completo || apiUser.nombre_usuario,
    role:            apiUser.rol,
    business_id:     apiUser.business_id || 1,
    nombre_usuario:  apiUser.nombre_usuario,
    nombre_completo: apiUser.nombre_completo,
    rol:             apiUser.rol,
    perfil_id:       apiUser.perfil_id || null,
    perfil_es_admin: !!apiUser.perfil_es_admin,
    business_name:   empresa?.razon_social || process.env.REACT_APP_NAME || 'Sistema de Gestión',
    business_logo:   empresa?.logo || null,
  };
}

async function fetchMenuKeys(perfilId, esAdmin) {
  // null = ver todo (sin restricción)
  if (!perfilId || esAdmin) return null;
  try {
    const r = await api.get(`/api/perfiles/${perfilId}/items`);
    return new Set(r.data?.items || []);
  } catch {
    return null; // fallback: ver todo
  }
}

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [empresa, setEmpresa]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [menuKeys, setMenuKeys]   = useState(null); // null = sin restricción
  const inactivityTimer           = useRef(null);

  useEffect(() => {
    const token   = localStorage.getItem('token');
    const stored  = localStorage.getItem('user');
    if (token && stored) {
      try {
        const apiUser = JSON.parse(stored);
        getEmpresa()
          .then(r => { setEmpresa(r.data.empresa || null); return buildUser(apiUser, r.data.empresa); })
          .catch(() => buildUser(apiUser, null))
          .then(async (u) => {
            setUser(u);
            setMenuKeys(await fetchMenuKeys(u.perfil_id, u.perfil_es_admin));
          })
          .finally(() => setLoading(false));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (nombre_usuario, contrasena) => {
    const res     = await apiLogin(nombre_usuario, contrasena);
    const { token, user: apiUser } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(apiUser));

    let empresaData = null;
    try {
      const er = await getEmpresa();
      empresaData = er.data.empresa;
    } catch {}

    setEmpresa(empresaData);
    const u = buildUser(apiUser, empresaData);
    setUser(u);
    setMenuKeys(await fetchMenuKeys(u.perfil_id, u.perfil_es_admin));
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setMenuKeys(null);
  };

  const refreshMenuKeys = async () => {
    if (!user) return;
    setMenuKeys(await fetchMenuKeys(user.perfil_id, user.perfil_es_admin));
  };

  const refreshEmpresa = async () => {
    try {
      const er = await getEmpresa();
      setEmpresa(er.data.empresa || null);
    } catch {}
  };

  // ── Timer de inactividad: cierra sesión del usuario tras 30 min sin actividad ──
  useEffect(() => {
    if (!user) {
      clearTimeout(inactivityTimer.current);
      return;
    }

    const reset = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setMenuKeys(null);
      }, INACTIVITY_MS);
    };

    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset(); // inicia el timer al entrar

    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, reset));
      clearTimeout(inactivityTimer.current);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      empresa,
      login,
      logout,
      isAuthenticated: !!user,
      loading,
      isSuperAdmin: false,
      menuKeys,
      refreshMenuKeys,
      refreshEmpresa,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
